import { Duration } from "luxon"

export type AudioRecorderEvent = {
  detail?: number | DOMException
} & Event

type AudioRecorderEventListener = (e: AudioRecorderEvent) => void

const EXPECTED_SETUP_ERROR_NAMES = ["NotAllowedError", "NotFoundError", "NotReadableError"]

export function isExpectedSetupError(error: unknown): boolean {
  return error instanceof DOMException && EXPECTED_SETUP_ERROR_NAMES.includes(error.name)
}

type EventName ="setupComplete" | "setupError" | "processing" | "recordingProgress" | "recordingComplete"

export default class AudioRecorder extends EventTarget {
  audioEl: HTMLAudioElement
  chunks: Blob[] = []
  mediaRecorder?: MediaRecorder
  timer?: ReturnType<typeof setInterval>
  recording: Blob | undefined

  constructor(audioEl: HTMLAudioElement) {
    super()

    this.audioEl = audioEl
    this.setup()
  }

  async setup() {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({
        audio: true,
      }).then(this.handleStream.bind(this))
        .catch(err => {
          const setupError = new CustomEvent("setupError", {
            detail: err
          })
          this.dispatchEvent(setupError)
          console.error(`getUserMedia error: ${err}`)
        })
    }
  }

  on(eventName: EventName, listener: AudioRecorderEventListener)  {
    this.addEventListener(eventName, listener)
    return this
  }

  off(eventName: EventName, listener: AudioRecorderEventListener)  {
    this.removeEventListener(eventName, listener)
    return this
  }

  handleStream(stream: MediaStream) {
    this.mediaRecorder = new MediaRecorder(stream)
    this.mediaRecorder.onstop = this.handleRecordingStopped.bind(this)
    this.mediaRecorder.onstart = this.handleRecordingStarted.bind(this)

    this.mediaRecorder.ondataavailable = (e: BlobEvent) => {
      this.chunks.push(e.data)
    }

    this.setupAudio()
    this.dispatchEvent(new Event("setupComplete"))
  }

  setupAudio() {
    this.audioEl.addEventListener("loadedmetadata", () => {
      this.fixMetadata()
    })
  }

  fixMetadata() {
    // Chrome does not calculate duration
    // https://issues.chromium.org/issues/40482588
    // so we need this workaround
    if (this.audioEl.duration == Infinity) {
      this.audioEl.addEventListener("timeupdate", () => {
        // reset back to beginning after workaround
        this.audioEl.currentTime = 0
        this.dispatchEvent(new Event("recordingComplete"))
      }, { once: true })

      // move position past the end
      this.audioEl.currentTime = 1e101
    }
  }

  start() {
    this.recording = undefined
    this.mediaRecorder?.start()
  }

  stop() {
    clearInterval(this.timer)
    this.mediaRecorder?.stop()
  }

  handleRecordingStarted() {
    const startTime = Date.now()
    this.timer = setInterval(() => {
      const seconds = Math.floor(((Date.now() - startTime) / 1000))
      const elapsedTime = Duration.
        fromObject({ seconds }).
        toMillis()

      const event = new CustomEvent("recordingProgress", {
        detail: elapsedTime
      })
      this.dispatchEvent(event)
    }, 1000)
  }

  handleRecordingStopped() {
    this.recording = new Blob(this.chunks, { type: "audio/ogg; codecs=opus" })
    this.chunks = []
    const audioURL = window.URL.createObjectURL(this.recording)

    if (this.audioEl) {
      this.audioEl.src = audioURL
    }

    this.dispatchEvent(new Event("processing"))
  }

}
